import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Gamepad2, Star, Zap, Brain, Puzzle, Target, Trophy,
  Clock, ChevronRight, Lock, Rocket, Sword, Slice, Shield,
  Cat, Bus, Music, Microscope, Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const GamesPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);

  // Detect if we're in admin preview mode
  const isAdminPreview = location.pathname.startsWith('/admin/preview');
  const basePath = isAdminPreview ? '/admin/preview' : '/student';

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/v2/student/stats');
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, [api]);

  const userPoints = user?.total_points || 0;

  const games = [
    {
      id: 'memory-match',
      name: 'Memory Match',
      description: 'Test your memory by matching pairs of cards',
      icon: Brain,
      color: 'purple',
      points: 50,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/memory-match?topic=ks2-maths-general&difficulty=easy`
    },
    {
      id: 'quick-quiz',
      name: 'Quick Quiz Rush',
      description: 'Answer as many questions as you can in 60 seconds!',
      icon: Zap,
      color: 'amber',
      points: 100,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/quick-quiz?subject=maths`
    },
    {
      id: 'word-puzzle',
      name: 'Word Puzzle',
      description: 'Find hidden words in the letter grid',
      icon: Puzzle,
      color: 'pink',
      points: 75,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/word-puzzle?difficulty=easy`
    },
    {
      id: 'number-ninja',
      name: 'Number Ninja',
      description: 'Slice through maths problems at lightning speed',
      icon: Target,
      color: 'emerald',
      points: 100,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/number-ninja`
    },
    {
      id: 'spelling-bee',
      name: 'Spelling Bee',
      description: 'How good is your spelling? Find out!',
      icon: Trophy,
      color: 'blue',
      points: 80,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/spelling-bee`
    },
    {
      id: 'science-lab',
      name: 'Science Lab',
      description: 'Mix virtual chemicals and conduct experiments',
      icon: Gamepad2,
      color: 'teal',
      points: 150,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/science-lab`,
      isScience: true
    },
    {
      id: 'virtual-microscope',
      name: 'Virtual Microscope',
      description: 'Explore cells and organisms under the microscope!',
      icon: Microscope,
      color: 'indigo',
      points: 160,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/virtual-microscope`,
      isNew: true,
      isScience: true
    },
    {
      id: 'circuit-builder',
      name: 'Circuit Builder',
      description: 'Build electrical circuits and learn about electricity!',
      icon: Zap,
      color: 'amber',
      points: 140,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/circuit-builder`,
      isNew: true,
      isScience: true
    },
    {
      id: 'solar-system',
      name: 'Solar System Explorer',
      description: 'Journey through planets and discover space facts!',
      icon: Globe,
      color: 'purple',
      points: 180,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/solar-system`,
      isNew: true,
      isScience: true
    },
    {
      id: 'space-math',
      name: 'Space Math Invaders',
      description: 'Shoot down alien equations with correct answers!',
      icon: Rocket,
      color: 'indigo',
      points: 120,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/space-math`,
      isNew: true
    },
    {
      id: 'word-builder',
      name: 'Word Builder Quest',
      description: 'Build words letter-by-letter in this RPG adventure!',
      icon: Sword,
      color: 'violet',
      points: 100,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/word-builder`,
      isNew: true
    },
    {
      id: 'fraction-frenzy',
      name: 'Fraction Frenzy',
      description: 'Collect equivalent fractions in this arcade game!',
      icon: Slice,
      color: 'orange',
      points: 110,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/fraction-frenzy`,
      isNew: true
    },
    {
      id: 'grammar-guardian',
      name: 'Grammar Guardian',
      description: 'Defend your castle by fixing grammar errors!',
      icon: Shield,
      color: 'red',
      points: 130,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/grammar-guardian`,
      isNew: true
    },
    // KS1 Phonics Games
    {
      id: 'letter-sounds-safari',
      name: 'Letter Sounds Safari',
      description: 'Match letters to their sounds in the jungle!',
      icon: Cat,
      color: 'emerald',
      points: 80,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/letter-sounds-safari`,
      isNew: true,
      isPhonics: true,
      ks1Only: true
    },
    {
      id: 'blending-bus',
      name: 'Blending Bus',
      description: 'Blend letter sounds to make words!',
      icon: Bus,
      color: 'yellow',
      points: 90,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/blending-bus`,
      isNew: true,
      isPhonics: true,
      ks1Only: true
    },
    {
      id: 'rhyme-time-rocket',
      name: 'Rhyme Time Rocket',
      description: 'Launch rockets by finding rhyming words!',
      icon: Music,
      color: 'purple',
      points: 85,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/rhyme-time-rocket`,
      isNew: true,
      isPhonics: true,
      ks1Only: true
    },
    {
      id: 'phonics-pop',
      name: 'Phonics Pop',
      description: 'Pop balloons with matching letter sounds!',
      icon: Music,
      emoji: '🎈',
      color: 'pink',
      points: 75,
      unlockPoints: 0,
      available: true,
      path: `${basePath}/games/phonics-pop`,
      isNew: true,
      isPhonics: true,
      ks1Only: true
    }
  ];

  // Check if user should see phonics games (Year 1-2 or KS1)
  const showPhonicsGames = () => {
    if (isAdminPreview) return true; // Admin preview always shows all
    if (!user) return true;
    
    const yearGroup = user.year_group || user.recommended_year || 3;
    const keyStage = user.key_stage || '';
    
    // Show phonics for Year 1-2 or KS1
    return yearGroup <= 2 || keyStage === 'ks1';
  };

  const handlePlayGame = (game) => {
    if (game.path) {
      navigate(game.path);
    } else {
      // Coming soon for other games
      alert('This game is coming soon!');
    }
  };

  return (
    <StudentLayout>
      <div data-testid="games-page">
        {/* Header */}
        <div className="student-card bg-purple-100 border-purple-300 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-purple-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center animate-float">
                <Gamepad2 className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-800">Games Zone</h1>
                <p className="text-slate-600">Play games and earn bonus points!</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border-2 border-purple-300">
              <Star className="w-6 h-6 text-purple-500" />
              <span className="font-heading text-2xl font-bold text-purple-600">{userPoints}</span>
              <span className="text-slate-600">points</span>
            </div>
          </div>
        </div>

        {/* KS1 Phonics Games Section - Only show for Year 1-2 or KS1 students */}
        {showPhonicsGames() && (
          <>
            <div className="mt-8 mb-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                  <span className="text-xl">🔤</span>
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-slate-800">Phonics Games</h2>
                  <p className="text-slate-600 text-sm">Perfect for KS1 learners!</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {games.filter(g => g.isPhonics).map(game => (
            <div 
              key={game.id}
              className="student-card relative overflow-hidden bg-gradient-to-br from-white to-emerald-50 border-emerald-200"
              data-testid={`game-${game.id}`}
            >
              {game.isNew && (
                <div className="absolute top-2 left-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  NEW!
                </div>
              )}
              <div className="absolute top-2 right-2 bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full">
                PHONICS
              </div>
              
              <div className={`w-12 h-12 bg-${game.color}-400 rounded-xl border-2 border-gray-800 flex items-center justify-center mb-3 mx-auto`}>
                {game.emoji ? (
                  <span className="text-2xl">{game.emoji}</span>
                ) : (
                  <game.icon className="w-6 h-6 text-white" />
                )}
              </div>
              
              <h3 className="font-heading text-lg font-bold text-slate-800 mb-1 text-center">{game.name}</h3>
              <p className="text-slate-600 text-xs mb-3 text-center">{game.description}</p>
              
              <div className="flex items-center justify-center gap-1 text-amber-600 mb-3">
                <Star className="w-4 h-4" />
                <span className="font-bold text-sm">+{game.points}</span>
              </div>

              <Button 
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm py-2"
                onClick={() => handlePlayGame(game)}
                data-testid={`play-${game.id}`}
              >
                Play
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ))}
        </div>
          </>
        )}

        {/* Science Simulations Section */}
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
              <span className="text-xl">🔬</span>
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-800">Science Simulations</h2>
              <p className="text-slate-600 text-sm">Explore science through experiments!</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {games.filter(g => g.isScience).map(game => (
            <div 
              key={game.id}
              className="student-card relative overflow-hidden bg-gradient-to-br from-white to-teal-50 border-teal-200"
              data-testid={`game-${game.id}`}
            >
              {game.isNew && (
                <div className="absolute top-2 left-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                  NEW!
                </div>
              )}
              <div className="absolute top-2 right-2 bg-teal-100 text-teal-700 text-xs font-bold px-2 py-0.5 rounded-full">
                SCIENCE
              </div>
              
              <div className={`w-12 h-12 bg-${game.color}-400 rounded-xl border-2 border-gray-800 flex items-center justify-center mb-3 mx-auto`}>
                <game.icon className="w-6 h-6 text-white" />
              </div>
              
              <h3 className="font-heading text-lg font-bold text-slate-800 mb-1 text-center">{game.name}</h3>
              <p className="text-slate-600 text-xs mb-3 text-center">{game.description}</p>
              
              <div className="flex items-center justify-center gap-1 text-amber-600 mb-3">
                <Star className="w-4 h-4" />
                <span className="font-bold text-sm">+{game.points}</span>
              </div>

              <Button 
                className="w-full bg-teal-500 hover:bg-teal-600 text-white text-sm py-2"
                onClick={() => handlePlayGame(game)}
                data-testid={`play-${game.id}`}
              >
                Play
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ))}
        </div>

        {/* Arcade Games Section */}
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
              <span className="text-xl">🎮</span>
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-800">Arcade Games</h2>
              <p className="text-slate-600 text-sm">Action-packed learning!</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {games.filter(g => g.isNew && !g.isPhonics && !g.isScience).map(game => (
            <div 
              key={game.id}
              className="student-card relative overflow-hidden bg-gradient-to-br from-white to-indigo-50 border-indigo-200"
              data-testid={`game-${game.id}`}
            >
              <div className="absolute top-2 left-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                NEW!
              </div>
              <div className="absolute top-2 right-2 bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">
                ARCADE
              </div>
              
              <div className={`w-12 h-12 bg-${game.color}-400 rounded-xl border-2 border-gray-800 flex items-center justify-center mb-3 mx-auto`}>
                <game.icon className="w-6 h-6 text-white" />
              </div>
              
              <h3 className="font-heading text-lg font-bold text-slate-800 mb-1 text-center">{game.name}</h3>
              <p className="text-slate-600 text-xs mb-3 text-center">{game.description}</p>
              
              <div className="flex items-center justify-center gap-1 text-amber-600 mb-3">
                <Star className="w-4 h-4" />
                <span className="font-bold text-sm">+{game.points}</span>
              </div>

              <Button 
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white text-sm py-2"
                onClick={() => handlePlayGame(game)}
                data-testid={`play-${game.id}`}
              >
                Play
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ))}
        </div>

        {/* Classic Games Section */}
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
              <Gamepad2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-800">Classic Games</h2>
              <p className="text-slate-600 text-sm">Educational favorites!</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {games.filter(g => !g.isNew).map(game => (
            <div 
              key={game.id}
              className={`student-card relative overflow-hidden ${!game.available ? 'opacity-75' : ''}`}
              data-testid={`game-${game.id}`}
            >
              {game.isNew && (
                <div className="absolute top-3 left-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                  NEW!
                </div>
              )}
              {!game.available && (
                <div className="absolute top-3 right-3 bg-slate-200 rounded-full p-2">
                  <Lock className="w-4 h-4 text-slate-500" />
                </div>
              )}
              
              <div className={`w-14 h-14 bg-${game.color}-400 rounded-2xl border-2 border-gray-800 flex items-center justify-center mb-4`}>
                <game.icon className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="font-heading text-xl font-bold text-slate-800 mb-1">{game.name}</h3>
              <p className="text-slate-600 text-sm mb-4">{game.description}</p>
              
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-1 text-amber-600">
                  <Star className="w-5 h-5" />
                  <span className="font-bold">+{game.points}</span>
                </div>
                {!game.available && (
                  <span className="text-xs text-slate-500">
                    Unlock at {game.unlockPoints} points
                  </span>
                )}
              </div>

              {game.available ? (
                <Button 
                  className="w-full student-btn"
                  onClick={() => handlePlayGame(game)}
                  data-testid={`play-${game.id}`}
                >
                  Play Now
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              ) : (
                <Button 
                  className="w-full bg-slate-200 text-slate-500 cursor-not-allowed"
                  disabled
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Locked
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Tips */}
        <div className="mt-8 student-card bg-amber-50 border-amber-300">
          <h3 className="font-heading text-lg font-bold text-slate-800 mb-2">Pro Tips!</h3>
          <ul className="space-y-2 text-slate-600">
            <li className="flex items-start gap-2">
              <span className="text-amber-500">•</span>
              Complete lessons to earn points and unlock more games
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500">•</span>
              Each game can be played multiple times for bonus points
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500">•</span>
              Higher scores mean more points - aim for the stars!
            </li>
          </ul>
        </div>
      </div>
    </StudentLayout>
  );
};

export default GamesPage;
